import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import userLogin from '../views/userLogin.vue'
import AppLayout from '../AppLayout.vue'
import DataImportView from '../views/DataImportView.vue'
import HomeView from '../views/HomeView.vue'
import InsureApplyView from '../views/InsureApplyView.vue'
import AdminRoutes from './AdminRoutes'
import { menuAllowedByRole } from '@/const/menus'

// Fix navigation errors (Duplicated, Redirected, Cancelled)
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push (location) {
  return originalPush.call(this, location).catch(err => {
    // Ignore navigation errors that are safe to suppress
    if (
      err.name === 'NavigationDuplicated' ||
      err.name === 'NavigationCancelled' ||
      err.message.includes('Redirected when going from') ||
      err.message.includes('Navigation cancelled')
    ) {
      return err
    }
    // Throw other errors
    throw err
  })
}
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace (location) {
  return originalReplace.call(this, location).catch(err => {
    // Ignore navigation errors that are safe to suppress
    if (
      err.name === 'NavigationDuplicated' ||
      err.name === 'NavigationCancelled' ||
      err.message.includes('Redirected when going from') ||
      err.message.includes('Navigation cancelled')
    ) {
      return err
    }
    // Throw other errors
    throw err
  })
}

Vue.use(VueRouter)

const isAhicOrg = (user) => String(user?.organize || '').trim().toLowerCase() === 'ahic'

// 普通用户路由
const userRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: userLogin
  },
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: 'ins'
      },
      {
        path: 'ins',
        name: 'Home',
        component: HomeView,
        meta: { menu: 'home' }
      },
      {
        path: 'data-import',
        name: 'DataImport',
        component: DataImportView,
        meta: { menu: 'data_import' }
      },
      {
        path: 'map-view',
        redirect: 'insure-apply'
      },
      {
        path: 'insure-apply',
        name: 'InsureApply',
        component: InsureApplyView,
        meta: { menu: 'insure_apply' }
      },
      {
        path: 'policy-list',
        name: 'PolicyList',
        component: () => import('@/views/PolicyListView.vue'),
        meta: { menu: 'policy_list' }
      },
      {
        path: 'policy-edit/:bdh/:nhdm/:zwlx',
        name: 'PolicyEdit',
        component: () => import('@/views/PolicyEditView.vue'),
        meta: { menu: 'insure_apply' }
      },
      {
        path: 'policy-plot-details',
        name: 'PolicyPlotDetails',
        component: () => import('@/views/PolicyPlotDetailsView.vue')
      },
      {
        path: 'insurance-results',
        name: 'InsuranceResults',
        component: () => import('@/views/InsuranceResultsView.vue'),
        meta: { menu: 'insurance_results' }
      },
      {
        path: 'insurance-results/policy-crop-report',
        name: 'InsurancePolicyCropReport',
        component: () => import('@/views/insurance-results/InsurancePolicyCropReportView.vue')
      },
      {
        path: 'insurance-results/plot-area-report',
        name: 'InsurancePlotAreaReport',
        component: () => import('@/views/insurance-results/InsurancePlotAreaReportView.vue')
      },
      {
        path: 'insurance-results/underwriting-stat-report',
        name: 'InsuranceUnderwritingStatReport',
        component: () => import('@/views/insurance-results/InsuranceUnderwritingStatReportView.vue')
      },
      {
        path: 'claim',
        name: 'Claim',
        component: () => import('@/views/claim/ClaimView.vue'),
        meta: { menu: 'claim' }
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/admin/Dashboard.vue'),
        meta: { menu: 'dashboard' }
      },
      {
        path: 'dashboard/reports/:reportKey',
        name: 'ReportPlatform',
        component: () => import('@/views/ReportPlatformView.vue')
      }
    ]
  }
]

// 合并所有路由
const routes = [
  ...userRoutes,
  ...AdminRoutes
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  const jwt = localStorage.getItem('jwt')
  let userRole = store.state.userRole

  // Restore session if token exists but user not loaded
  if (jwt && !store.state.user) {
    try {
      const user = await store.dispatch('fetchUser')
      // Support both old role object and new type field
      if (user.type) {
        userRole = user.type;
      } else if (user.role && user.role.role_code) {
        userRole = user.role.role_code;
      } else {
        userRole = 'CLIENT';
      }
    } catch (e) {
      console.warn('Session restoration failed:', e)
      // Token invalid
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }
  }

  // Normalize checking
  const isAdmin = userRole === 'ADMIN' || userRole === 'admin';

  console.log(`Navigating from ${from.path} to ${to.path}`)
  console.log('User role:', userRole)
  console.log('Route requires auth:', to.matched.some(record => record.meta.requiresAuth))
  console.log('Route requires admin:', to.matched.some(record => record.meta.requiresAdmin))

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!jwt) {
      console.log('No JWT, redirecting to login')
      next({ path: '/login', query: { redirect: to.fullPath } })
    } else {
      if (to.matched.some(record => record.meta.requiresAdmin)) {
        if (isAdmin) {
          console.log('Admin access granted')
          next()
        } else {
          console.log('Admin access denied, redirecting to /ins')
          next('/ins')
        }
      } else {
        if (to.name === 'ReportPlatform' && isAhicOrg(store.state.user)) {
          console.log('AHIC report platform access denied, redirecting to /dashboard')
          next('/dashboard')
          return
        }
        // 角色菜单权限：无该菜单权限的账号不允许直达路由
        const requiredMenu = to.matched
          .map(record => record.meta && record.meta.menu)
          .filter(Boolean)
          .pop()
        if (requiredMenu && !menuAllowedByRole(store.state.user && store.state.user.role, requiredMenu)) {
          console.log(`Menu "${requiredMenu}" denied, redirecting to /claim`)
          next('/claim')
          return
        }
        console.log('Regular auth route, proceeding')
        next()
      }
    }
  } else if (to.path === '/login') {
    if (jwt) {
      console.log('Already logged in, redirecting from login')
      if (isAdmin) {
        next('/admin')
      } else {
        next('/ins')
      }
    } else {
      console.log('Login page access allowed')
      next()
    }
  } else {
    console.log('Public route, proceeding')
    next()
  }
})

export default router
